import apiClient from './apiClient';
import { Nurse, NurseListResponse, NurseSearchParams } from '../types/nurse';
// 外部データベースのAPIエンドポイント
const BASE_URL = '/external/nurses';
// レスポンスを処理する関数
const processResponse = (data: any): NurseListResponse => {
  // レスポンスがページネーション形式の場合
  if (data && ('results' in data)) {
    return data;
  }
  // 配列の場合（ページネーションなし）
  else if (Array.isArray(data)) {
    const nurses = data as Nurse[];
    return {
      count: nurses.length,
      next: null,
      previous: null,
      results: nurses
    };
  }
  // その他の形式
  else {
    console.warn('不明なレスポンス形式:', data);
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  }
};
// リクエストIDの保存用マップ
const lastRequestIdsMap = new Map<string, number>();
// 看護師一覧を取得
export const getNurses = async(params: NurseSearchParams = {}): Promise<NurseListResponse> => {
  const queryParams = { ...params };
  // リクエストIDの生成（パラメータをJSON文字列化）
  const requestId = JSON.stringify(queryParams);
  const now = Date.now();
  lastRequestIdsMap.set(requestId, now);
  try {
    // 配列パラメータの有無を確認
    const hasArrayParams =
      (queryParams.role_type && Array.isArray(queryParams.role_type) && queryParams.role_type.length > 0) ||
      (queryParams.workshop_status && Array.isArray(queryParams.workshop_status) && queryParams.workshop_status.length > 0);
    if (hasArrayParams) {
      // URLSearchParamsを使用してパラメータを生成
      const urlParams = new URLSearchParams();
      // role_typeパラメータの処理 - DRF標準の'in'フィルターに対応
      if (queryParams.role_type && Array.isArray(queryParams.role_type) && queryParams.role_type.length > 0) {
        const roleTypes = queryParams.role_type;
        delete queryParams.role_type;
        // カンマ区切りのリストとして送信
        urlParams.append('role_type__in', roleTypes.join(','));
      }
      // workshop_statusパラメータの処理 - DRF標準の'in'フィルターに対応
      if (queryParams.workshop_status && Array.isArray(queryParams.workshop_status) && queryParams.workshop_status.length > 0) {
        const workshopStatuses = queryParams.workshop_status;
        delete queryParams.workshop_status;
        // カンマ区切りのリストとして送信
        urlParams.append('workshop_status__in', workshopStatuses.join(','));
      }
      // 残りのパラメータをコピー
      Object.entries(queryParams).forEach(([ key, value ]) => {
        if (value !== undefined) {
          urlParams.append(key, String(value));
        }
      });
      const fullUrl = `${BASE_URL}?${urlParams.toString()}`;
      const response = await apiClient.get<NurseListResponse>(fullUrl);
      return processResponse(response.data);
    } else {
      // 通常のパラメータ処理
      const response = await apiClient.get<NurseListResponse>(BASE_URL, { params: queryParams });
      return processResponse(response.data);
    }
  } catch (error) {
    console.error('API呼び出しエラー:', error);
    throw error;
  }
};
// 看護師詳細を取得
export const getNurse = async(id: number): Promise<Nurse> => {
  const response = await apiClient.get<Nurse>(`${BASE_URL}/${id}/`);
  return response.data;
};
// 看護師メモを更新
export const updateNurseMemo = async(id: number, memo: string): Promise<Nurse> => {
  const response = await apiClient.put<Nurse>(`${BASE_URL}/${id}/`, { senior_memo: memo });
  return response.data;
};
// アクティブな看護師一覧を取得
export const getActiveNurses = async(params: NurseSearchParams = {}): Promise<NurseListResponse> => {
  const response = await apiClient.get<NurseListResponse>(`${BASE_URL}/active/`, { params });
  return response.data;
};
// 上席相談ルーム登録可能な看護師一覧を取得（上席看護師からスタッフ看護師を検索）
export const getAvailableAssignedNurses = async(nurseId: number): Promise<Nurse[]> => {
  const response = await apiClient.get<Nurse[]>(`${BASE_URL}/${nurseId}/assigned_nurses/available/`);
  return response.data;
};
// 上席相談ルーム登録可能な上席看護師一覧を取得（スタッフ看護師から上席看護師を検索）
export const getAvailableSupportNurses = async(nurseId: number): Promise<Nurse[]> => {
  const response = await apiClient.get<Nurse[]>(`${BASE_URL}/${nurseId}/support_nurses/available/`);
  return response.data;
};

// 上席相談ルームを登録
export interface AssignNurseRequest {
  child_nurse_id: number;
}

export interface AssignParentNurseRequest {
  parent_nurse_id: number;
}

export interface AssignNurseResponse {
  success: boolean;
  message: string;
  data: any;
}

// 上席看護師から一般看護師を登録（従来のパターン）
export const assignNurse = async(nurseId: number, childNurseId: number): Promise<AssignNurseResponse> => {
  const response = await apiClient.post<AssignNurseResponse>(
    `${BASE_URL}/${nurseId}/assigned_nurses/`,
    { child_nurse_id: childNurseId }
  );
  return response.data;
};
// 一般看護師から上席看護師を登録（逆のパターン）
export const assignSupportNurse = async(nurseId: number, parentNurseId: number): Promise<AssignNurseResponse> => {
  const response = await apiClient.post<AssignNurseResponse>(
    `${BASE_URL}/${nurseId}/support_nurses/`,
    { parent_nurse_id: parentNurseId }
  );
  return response.data;
};

// CSV出力プレビューを取得
export interface CSVExportPreviewResponse {
  headers: Array<{ field: string; display: string }>;
  all_headers: Array<{ field: string; display: string }>;
  personal_fields: string[];
  rows: Array<Record<string, any>>;
  total_count: number;
}

export interface CSVExportParams extends NurseSearchParams {
  exclude_fields?: string[];
  preview_rows?: number;
}

export const getNursesCSVPreview = async(params: CSVExportParams = {}): Promise<CSVExportPreviewResponse> => {
  // 一覧エンドポイントにoutput_format=csv_previewパラメータを追加
  const queryParams = { ...params, output_format: 'csv_preview', preview_rows: 10 };
  // 配列パラメータの処理（既存のgetNursesと同じ）
  const hasArrayParams =
    (queryParams.role_type && Array.isArray(queryParams.role_type) && queryParams.role_type.length > 0) ||
    (queryParams.workshop_status && Array.isArray(queryParams.workshop_status) && queryParams.workshop_status.length > 0);
  if (hasArrayParams) {
    const urlParams = new URLSearchParams();
    if (queryParams.role_type && Array.isArray(queryParams.role_type) && queryParams.role_type.length > 0) {
      const roleTypes = queryParams.role_type;
      delete queryParams.role_type;
      urlParams.append('role_type__in', roleTypes.join(','));
    }
    if (queryParams.workshop_status && Array.isArray(queryParams.workshop_status) && queryParams.workshop_status.length > 0) {
      const workshopStatuses = queryParams.workshop_status;
      delete queryParams.workshop_status;
      urlParams.append('workshop_status__in', workshopStatuses.join(','));
    }
    Object.entries(queryParams).forEach(([ key, value ]) => {
      if (value !== undefined) {
        urlParams.append(key, String(value));
      }
    });
    const fullUrl = `${BASE_URL}/?${urlParams.toString()}`;
    const response = await apiClient.get<CSVExportPreviewResponse>(fullUrl);
    return response.data;
  } else {
    const response = await apiClient.get<CSVExportPreviewResponse>(`${BASE_URL}/`, { params: queryParams });
    return response.data;
  }
};

export const generatePasswordResetUrl = async(id: number, withNotification: boolean): Promise<{
  reset_url: string;
  expires_at: string;
}> => {
  const response = await apiClient.post(`${BASE_URL}/${id}/password_reset/`, { 
    with_notification: withNotification 
  });
  return response.data;
};

const nursesApi = {
  getNurses,
  getNurse,
  updateNurseMemo,
  getActiveNurses,
  getAvailableAssignedNurses,
  getAvailableSupportNurses,
  assignNurse,
  assignSupportNurse,
  getNursesCSVPreview,
};
export default nursesApi;
